import { FLOW_STEPS } from '@/data/flowSteps'
import { Header } from '@/layout/Header'
import { Stepper } from '@/layout/Stepper'
import { useFlowStore } from '@/store/flowStore'
import { useAuth } from '@/lib/useAuth'
import { clearSessionToken } from '@/lib/auth'
import { signOut } from '@/lib/auth-client'
import { Link, Outlet, createRootRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'

const OFFSET = 80

const STEPPER_ROUTES = ['/objetivo', '/consentimento']
const NO_LOGO_CLICK_ROUTES = [
  '/objetivo',
  '/carregando',
  '/resultado',
  '/contato',
  '/sucesso',
  '/checkout',
  '/pagamento',
]
const AUTH_HEADER_ROUTES = ['/objetivo', '/protocolo', '/conta', '/entrar']

type AuthHeaderMode = 'account-home' | 'account-subroute' | 'default'

function getAuthHeaderMode(pathname: string): AuthHeaderMode | null {
  if (pathname === '/conta') return 'account-home'
  if (pathname.startsWith('/conta/')) return 'account-subroute'
  if (AUTH_HEADER_ROUTES.includes(pathname)) return 'default'
  return null
}

function RootLayout() {
  const navigate = useNavigate()
  const prefersReducedMotion = useReducedMotion()
  const { steps, index, completed, resetAll } = useFlowStore()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const showStepper = STEPPER_ROUTES.includes(pathname)
  const showProgress = pathname === '/chat'
  const { isLoggedIn } = useAuth()
  const authHeaderMode = getAuthHeaderMode(pathname)

  const progress = showProgress
    ? completed
      ? 100
      : Math.min((index / steps.length) * 100, 100)
    : undefined

  const handleLogoClick = NO_LOGO_CLICK_ROUTES.includes(pathname)
    ? undefined
    : () => {
        resetAll()
        navigate({ to: '/objetivo' })
      }

  const handleLogout = async () => {
    try { await signOut() } catch { /* ignore */ }
    clearSessionToken()
    navigate({ to: '/objetivo' })
  }

  const stepperIndex = pathname === '/objetivo' ? 0 : 1

  return (
    <div className="flex flex-col h-dvh bg-[#EDEAE5] overflow-hidden">
      <Header
        onLogoClick={handleLogoClick}
        progress={progress}
        rightContent={
          authHeaderMode ? (
            isLoggedIn ? (
              <div className="flex items-center gap-3">
                {authHeaderMode !== 'account-subroute' && (
                  <>
                    <Link
                      to="/conta/configuracoes"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100"
                      aria-label="Configuracoes da conta"
                      title="Configuracoes"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33h.01A1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                    </Link>
                    <Link
                      to="/conta"
                      className="text-sm font-medium text-gray-900 hover:text-gray-700 transition-colors whitespace-nowrap"
                    >
                      Minha Conta
                    </Link>
                  </>
                )}
                {authHeaderMode === 'account-subroute' && (
                  <Link
                    to="/conta"
                    className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap"
                  >
                    Minha Conta
                  </Link>
                )}
                <span className="text-gray-300">|</span>
                <button
                  onClick={handleLogout}
                  className="text-sm text-gray-400 hover:text-red-500 transition-colors whitespace-nowrap"
                >
                  Sair
                </button>
              </div>
            ) : (
              <Link
                to="/entrar"
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap"
              >
                Login
              </Link>
            )
          ) : undefined
        }
      >
        {showStepper && <Stepper steps={FLOW_STEPS} activeIndex={stepperIndex} />}
      </Header>

      <AnimatePresence initial={false} mode="popLayout">
        <motion.div
          key={pathname}
          initial={prefersReducedMotion ? false : { x: OFFSET, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          className="flex-1 flex flex-col overflow-hidden min-h-0 pt-[var(--header-height)]"
        >
          <Outlet />
        </motion.div>
      </AnimatePresence>

      {import.meta.env.DEV ? <TanStackRouterDevtools /> : null}
    </div>
  )
}

export const Route = createRootRoute({
  component: RootLayout,
})
